import { useEffect, useMemo, useRef, useState, type CSSProperties, type DragEvent } from 'react';
import {
  Background, BackgroundVariant, Controls, Handle, MarkerType, MiniMap, NodeResizer, Position, ReactFlow,
  addEdge, useEdgesState, useNodesState,
  type Connection, type Edge, type Node, type NodeProps, type ReactFlowInstance,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Button, Switch, Tag, Tooltip, Typography } from 'antd';
import { ApartmentOutlined, FullscreenExitOutlined, FullscreenOutlined, RedoOutlined, UndoOutlined } from '@ant-design/icons';
import { getRuleNodeDefinition, getRuleNodeOutputs } from '@dtwin/shared';
import type {
  RuleChainEdge, RuleChainGraph, RuleChainNode, RuleChainNodeResult, RuleChainNodeType, RuleChainTestResult,
} from '../../api/ruleChains';

const { Text } = Typography;

export interface RuleChainCanvasProps {
  graph: RuleChainGraph;
  onChange: (graph: RuleChainGraph) => void;
  testResult?: RuleChainTestResult | null;
  selectedNodeId?: string | null;
  selectedEdgeId?: string | null;
  onSelectNode?: (id: string | null) => void;
  onSelectEdge?: (id: string | null) => void;
}

type RuleNodeData = {
  node: RuleChainNode;
  test?: RuleChainNodeResult;
  onResizeEnd?: (id: string, width: number, height: number) => void;
};

const NODE_COLORS: Record<string, string> = {
  source: '#1677ff',
  filter: '#fa8c16',
  transform: '#722ed1',
  enrich: '#13c2c2',
  action: '#52c41a',
  route: '#eb2f96',
  debug: '#8c8c8c',
};

function summary(node: RuleChainNode): string {
  const c = node.config || {};
  const kind = node.kind || getRuleNodeDefinition(node.type)?.kind || '';
  switch (node.type) {
    case 'source':
      return `${String(c.triggerType || '-')}${c.topic ? ` · ${String(c.topic)}` : ''}${c.imei ? ` · ${String(c.imei)}` : ''}`;
    case 'filter':
      return String(c.expression || '（未配置表达式）');
    case 'transform':
      return String(c.script || '（未配置脚本）').split('\n')[0].slice(0, 48);
    case 'enrich':
      return `source=${String(c.source || '-')}`;
    case 'action':
      return `${kind || String(c.type || '-')}${(c.params as any)?.event ? ` · ${String((c.params as any).event)}` : ''}`;
    case 'flow':
      return `${kind || 'output'}${c.key ? ` · ${String(c.key)}` : ''}`;
    case 'route':
      return '按出边条件分支';
    case 'debug':
      return String(c.label || '调试输出');
    default:
      return '';
  }
}

function RuleNodeView({ data, selected }: NodeProps<Node<RuleNodeData>>) {
  const node = data.node;
  const test = data.test;
  const color = NODE_COLORS[node.type] || '#8c8c8c';
  const outputs = getRuleNodeOutputs(node);
  const multi = outputs.length > 1;
  const handleStyle: CSSProperties = { width: 14, height: 14, border: '2px solid #fff', background: '#1677ff' };
  const testColor = test?.status === 'success' ? '#52c41a' : test?.status === 'failed' ? '#ff4d4f' : '#faad14';
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        minWidth: 170,
        minHeight: 60,
        boxSizing: 'border-box',
        border: `2px solid ${selected ? '#1677ff' : test ? testColor : color}`,
        borderRadius: 8,
        background: 'var(--panel, #1f1f1f)',
        color: 'var(--text, #e6e6e6)',
        padding: '6px 8px',
        boxShadow: test ? `0 0 0 2px ${testColor}33` : undefined,
      }}
      data-testid={`rf-node-${node.id}`}
    >
      <NodeResizer
        minWidth={160}
        minHeight={60}
        maxWidth={800}
        maxHeight={500}
        isVisible={selected}
        onResizeEnd={(_, p) => data.onResizeEnd?.(node.id, p.width, p.height)}
      />
      <Handle type="target" position={Position.Top} style={handleStyle} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Tag color={color} style={{ marginInlineEnd: 0 }}>{node.type}</Tag>
        <Text strong style={{ fontSize: 12 }}>{node.name || node.id}</Text>
        {test && <Tag color={test.status === 'success' ? 'green' : test.status === 'failed' ? 'red' : 'orange'} style={{ marginInlineStart: 'auto', marginInlineEnd: 0 }}>
          {test.status}
        </Tag>}
      </div>
      <div style={{ fontSize: 11, color: '#aaa', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {summary(node)}
      </div>
      {multi ? outputs.map((o, i) => (
        <Handle
          key={o}
          id={o}
          type="source"
          position={Position.Bottom}
          style={{ ...handleStyle, left: `${((i + 1) / (outputs.length + 1)) * 100}%` }}
          title={o}
        />
      )) : <Handle type="source" position={Position.Bottom} style={handleStyle} />}
    </div>
  );
}

const nodeTypes = { ruleNode: RuleNodeView };

function positionFor(index: number) {
  return { x: 80 + (index % 3) * 230, y: 60 + Math.floor(index / 3) * 150 };
}

function toFlowNodes(
  graph: RuleChainGraph,
  testResult?: RuleChainTestResult | null,
  selectedNodeId?: string | null,
  onResizeEnd?: (id: string, width: number, height: number) => void,
): Node<RuleNodeData>[] {
  return (graph.nodes || []).map((n, i) => ({
    id: n.id,
    type: 'ruleNode',
    position: n.position || positionFor(i),
    width: n.width,
    height: n.height,
    selected: n.id === selectedNodeId,
    data: {
      node: n,
      test: testResult?.nodeResults?.find((r) => r.nodeId === n.id),
      onResizeEnd,
    },
  }));
}

function toFlowEdges(graph: RuleChainGraph, testResult?: RuleChainTestResult | null): Edge[] {
  const executed = new Set((testResult?.nodeResults || []).filter((r) => r.status !== 'skipped').map((r) => r.nodeId));
  return (graph.edges || []).map((e, i) => ({
    id: e.id || `${e.from}-${e.to}-${i}`,
    source: e.from,
    target: e.to,
    sourceHandle: e.fromPort,
    label: e.condition || e.fromPort || undefined,
    animated: !!(testResult && executed.has(e.from) && executed.has(e.to)),
    markerEnd: { type: MarkerType.ArrowClosed },
    style: { stroke: e.condition ? '#fa8c16' : '#8c8c8c' },
    labelStyle: { fill: '#fa8c16', fontSize: 10 },
  }));
}

export default function RuleChainCanvas({
  graph, onChange, testResult, selectedNodeId, selectedEdgeId, onSelectNode, onSelectEdge,
}: RuleChainCanvasProps) {
  const resizeHandlerRef = useRef<(id: string, width: number, height: number) => void>(() => undefined);
  const flowNodes = useMemo(
    () => toFlowNodes(graph, testResult, selectedNodeId, (id, width, height) => resizeHandlerRef.current(id, width, height)),
    [graph, testResult, selectedNodeId],
  );
  const flowEdges = useMemo(() => toFlowEdges(graph, testResult), [graph, testResult]);
  const [nodes, setNodes, onNodesChange] = useNodesState(flowNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(flowEdges);
  const [rf, setRf] = useState<ReactFlowInstance | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [autoFit, setAutoFit] = useState(() => {
    try { return localStorage.getItem('dtwin_rule_chain_auto_fit') === 'true'; } catch { return false; }
  });
  const [snapEnabled, setSnapEnabled] = useState(() => {
    try { return localStorage.getItem('dtwin_rule_chain_snap_grid') !== 'false'; } catch { return true; }
  });
  const historyRef = useRef<RuleChainGraph[]>([]);
  const futureRef = useRef<RuleChainGraph[]>([]);
  const lastGraphRef = useRef<RuleChainGraph>(graph);
  const restoringRef = useRef(false);
  const [historyTick, setHistoryTick] = useState(0);

  useEffect(() => {
    try { localStorage.setItem('dtwin_rule_chain_auto_fit', String(autoFit)); } catch { /* ignore */ }
  }, [autoFit]);

  useEffect(() => {
    try { localStorage.setItem('dtwin_rule_chain_snap_grid', String(snapEnabled)); } catch { /* ignore */ }
  }, [snapEnabled]);

  useEffect(() => {
    if (restoringRef.current) {
      restoringRef.current = false;
      lastGraphRef.current = graph;
      return;
    }
    if (lastGraphRef.current === graph) return;
    try {
      historyRef.current.push(JSON.parse(JSON.stringify(lastGraphRef.current)));
      if (historyRef.current.length > 50) historyRef.current.shift();
      futureRef.current = [];
      setHistoryTick((v) => v + 1);
    } catch { /* ignore */ }
    lastGraphRef.current = graph;
  }, [graph]);

  useEffect(() => {
    if (!isFullscreen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsFullscreen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isFullscreen]);

  useEffect(() => {
    setNodes(flowNodes);
    setEdges(flowEdges);
  }, [flowNodes, flowEdges, setNodes, setEdges]);

  const fitView = () => {
    try { rf?.fitView({ padding: 0.25 }); } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!rf || !autoFit) return;
    const t = setTimeout(fitView, 80);
    return () => clearTimeout(t);
  }, [rf, graph.nodes.length, autoFit]);

  const commit = (nextNodes: Node<RuleNodeData>[], nextEdges: Edge[]) => {
    const graphNodes: RuleChainNode[] = nextNodes.map((n) => ({
      ...(n.data.node as RuleChainNode),
      id: n.id,
      position: { x: Math.round(n.position.x), y: Math.round(n.position.y) },
      width: n.width ?? n.data.node.width,
      height: n.height ?? n.data.node.height,
    }));
    const graphEdges: RuleChainEdge[] = nextEdges.map((e) => ({
      id: e.id,
      from: e.source,
      to: e.target,
      condition: e.sourceHandle ? undefined : (typeof e.label === 'string' && e.label ? e.label : undefined),
      fromPort: e.sourceHandle || undefined,
    }));
    onChange({ nodes: graphNodes, edges: graphEdges });
  };

  resizeHandlerRef.current = (id, width, height) => {
    const next = nodes.map((n) => (n.id === id ? { ...n, width, height } : n));
    setNodes(next);
    commit(next, edges);
  };

  const undo = () => {
    const prev = historyRef.current.pop();
    if (!prev) return;
    try { futureRef.current.push(JSON.parse(JSON.stringify(graph))); } catch { /* ignore */ }
    restoringRef.current = true;
    onChange(prev);
    setHistoryTick((v) => v + 1);
  };

  const redo = () => {
    const next = futureRef.current.pop();
    if (!next) return;
    try { historyRef.current.push(JSON.parse(JSON.stringify(graph))); } catch { /* ignore */ }
    restoringRef.current = true;
    onChange(next);
    setHistoryTick((v) => v + 1);
  };

  const autoLayout = () => {
    const indeg = new Map(nodes.map((n) => [n.id, 0]));
    const adj = new Map(nodes.map((n) => [n.id, [] as string[]]));
    for (const e of edges) {
      if (!adj.has(e.source) || !indeg.has(e.target)) continue;
      adj.get(e.source)!.push(e.target);
      indeg.set(e.target, (indeg.get(e.target) || 0) + 1);
    }
    const level = new Map<string, number>();
    const queue = nodes.filter((n) => (indeg.get(n.id) || 0) === 0).map((n) => n.id);
    queue.forEach((id) => level.set(id, 0));
    while (queue.length) {
      const id = queue.shift()!;
      const l = level.get(id) || 0;
      for (const next of adj.get(id) || []) {
        if (!level.has(next) || (level.get(next) || 0) < l + 1) {
          level.set(next, l + 1);
          queue.push(next);
        }
      }
    }
    const buckets = new Map<number, string[]>();
    nodes.forEach((n) => {
      const l = level.get(n.id) ?? 0;
      buckets.set(l, [...(buckets.get(l) || []), n.id]);
    });
    const pos = new Map<string, { x: number; y: number }>();
    for (const [l, ids] of buckets) ids.forEach((id, i) => pos.set(id, { x: 80 + l * 260, y: 60 + i * 150 }));
    const next = nodes.map((n) => ({ ...n, position: pos.get(n.id) || n.position }));
    setNodes(next);
    commit(next, edges);
    if (autoFit) setTimeout(fitView, 50);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
      if (!(e.ctrlKey || e.metaKey)) return;
      if (e.key.toLowerCase() === 'z') { e.preventDefault(); if (e.shiftKey) redo(); else undo(); }
      else if (e.key.toLowerCase() === 'y') { e.preventDefault(); redo(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const onConnect = (conn: Connection) => {
    if (!conn.source || !conn.target) return;
    const next = addEdge({ ...conn, markerEnd: { type: MarkerType.ArrowClosed } }, edges);
    setEdges(next);
    commit(nodes, next);
  };

  const onDrop = (event: DragEvent) => {
    event.preventDefault();
    const raw = event.dataTransfer.getData('application/rule-node');
    if (!raw || !rf) return;
    let type: RuleChainNodeType;
    let kind: string | undefined;
    try {
      const parsed = JSON.parse(raw);
      type = parsed.type;
      kind = parsed.kind;
    } catch {
      type = raw as RuleChainNodeType;
    }
    if (!type) return;
    const pos = rf.screenToFlowPosition({ x: event.clientX, y: event.clientY });
    const id = `${type}-${Date.now().toString(36)}`;
    const def = kind ? getRuleNodeDefinition(type, kind) : undefined;
    const node: RuleChainNode = {
      id,
      type,
      ...(kind ? { kind } : {}),
      config: def
        ? { ...def.defaults }
        : type === 'source'
          ? { triggerType: 'device' }
          : type === 'action' && kind
            ? { type: kind, params: kind === 'webhook' ? { url: '' } : kind === 'command' ? { action: 'get_status' } : { event: 'rulechain:triggered' } }
            : {},
      position: { x: Math.round(pos.x), y: Math.round(pos.y) },
    };
    const nextNodes = [...nodes, { id, type: 'ruleNode', position: node.position!, data: { node } } as Node<RuleNodeData>];
    setNodes(nextNodes);
    commit(nextNodes, edges);
    onSelectNode?.(id);
  };

  const wrapperStyle: CSSProperties = isFullscreen
    ? { position: 'fixed', inset: 0, zIndex: 1000, height: '100vh', background: '#141414', borderRadius: 0, border: 'none' }
    : { position: 'relative', height: 560, border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8 };

  return (
    <div ref={wrapperRef} style={wrapperStyle} data-testid="rule-chain-canvas">
      <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 10, display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px', borderRadius: 6, background: 'rgba(20,20,20,0.92)', border: '1px solid rgba(255,255,255,0.14)' }}>
        <Text style={{ fontSize: 12 }}>自动适配</Text>
        <Switch size="small" checked={autoFit} onChange={setAutoFit} data-testid="rule-chain-auto-fit" />
        <Text style={{ fontSize: 12 }}>网格</Text>
        <Switch size="small" checked={snapEnabled} onChange={setSnapEnabled} data-testid="rule-chain-snap-grid" />
        <Tooltip title="撤销 (Ctrl+Z)"><Button size="small" icon={<UndoOutlined />} disabled={!historyRef.current.length} onClick={undo} data-testid="rule-chain-undo" /></Tooltip>
        <Tooltip title="重做 (Ctrl+Y)"><Button size="small" icon={<RedoOutlined />} disabled={!futureRef.current.length} onClick={redo} data-testid="rule-chain-redo" /></Tooltip>
        <Tooltip title="自动整理布局"><Button size="small" icon={<ApartmentOutlined />} onClick={autoLayout} data-testid="rule-chain-auto-layout" /></Tooltip>
        <Tooltip title="适配视图"><Button size="small" onClick={fitView} data-testid="rule-chain-fit-view">适配</Button></Tooltip>
        <Tooltip title={isFullscreen ? '退出全屏 (Esc)' : '全屏'}>
          <Button
            size="small"
            icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
            onClick={() => setIsFullscreen((v) => !v)}
            data-testid="rule-chain-fullscreen"
          />
        </Tooltip>
      </div>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange as any}
        connectionRadius={40}
        connectionDragThreshold={2}
        snapToGrid={snapEnabled}
        snapGrid={[16, 16]}
        selectionOnDrag
        panOnDrag={[1, 2]}
        multiSelectionKeyCode={['Control', 'Meta', 'Shift']}
        onEdgesChange={onEdgesChange as any}
        onConnect={onConnect}
        onInit={setRf}
        onDrop={onDrop}
        onDragOver={(e) => e.preventDefault()}
        onNodeDragStop={(_, node) => {
          const next = nodes.map((n) => (n.id === node.id ? { ...n, position: node.position } : n));
          setNodes(next);
          commit(next, edges);
        }}
        onNodeClick={(_, node) => onSelectNode?.(node.id)}
        onEdgeClick={(_, edge) => onSelectEdge?.(edge.id)}
        onPaneClick={() => { onSelectNode?.(null); onSelectEdge?.(null); }}
        onNodesDelete={(deleted) => {
          const ids = new Set(deleted.map((n) => n.id));
          const nextNodes = nodes.filter((n) => !ids.has(n.id));
          const nextEdges = edges.filter((e) => !ids.has(e.source) && !ids.has(e.target));
          setNodes(nextNodes);
          setEdges(nextEdges);
          commit(nextNodes, nextEdges);
        }}
        onEdgesDelete={(deleted) => {
          const ids = new Set(deleted.map((e) => e.id));
          const next = edges.filter((e) => !ids.has(e.id));
          setEdges(next);
          commit(nodes, next);
        }}
        fitView
        proOptions={{ hideAttribution: true }}
        defaultEdgeOptions={{ markerEnd: { type: MarkerType.ArrowClosed } }}
      >
        <Background variant={BackgroundVariant.Dots} gap={18} size={1} color="#333" />
        <MiniMap pannable zoomable style={{ background: '#141414' }} />
        <Controls />
      </ReactFlow>
    </div>
  );
}
